import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import TemplateFormField, { TemplateSelectItem } from 'pages/contract/components/TemplateFormField';
import { TicketFormGroupResult, TicketFormItemResult } from 'stores/TicketTypeStoreAdapter';

/**
 * 種別テンプレート（formGroups + unassignedItems）を動的フォームとして描画する。
 * 契約テンプレートの TemplateFormField を流用し、formType に応じた入力UIを出す。
 *
 * 値は itemKey -> string（単一値 or JSON配列文字列）で保持し、
 * 保存時に TicketFormValueInput[]（values は string[]）へ変換する（buildFormValues）。
 */

/** SELECT系（複数値）formType。JSON配列文字列で保持する。 */
const MULTI_FORM_TYPES = ['MULTI_SELECT', 'MULTI_CHECK', 'MULTI_CHECK_BUTTON', 'TECH_STACK_SELECT'];

type TicketFormFieldsProps = {
  /** 種別テンプレートのグループ（sort_order順・表示中のみ） */
  formGroups: TicketFormGroupResult[];
  /** 未所属項目 */
  unassignedItems: TicketFormItemResult[];
  /** itemKey -> 値（単一は文字列 / 複数は JSON配列文字列） */
  values: Record<string, string>;
  onChange: (itemKey: string, value: string) => void;
  disabled?: boolean;
};

const renderItems = (
  items: TicketFormItemResult[],
  values: Record<string, string>,
  onChange: (itemKey: string, value: string) => void,
  disabled: boolean,
) =>
  items
    .filter((it) => !it.deleted)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item) => {
      const selectItems: TemplateSelectItem[] = (item.selectItems || [])
        .filter((si) => !si.deleted)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((si) => ({ code: si.code, name: si.name }));
      return (
        <Box
          key={item.itemKey}
          data-toc-section
          id={`item-${item.itemKey}`}
          data-toc-label={item.label}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ mb: 0.5, display: 'block' }}
          >
            {item.label}
          </Typography>
          <TemplateFormField
            formType={item.formType}
            label={item.label}
            value={values[item.itemKey] ?? ''}
            onChange={(v) => onChange(item.itemKey, v)}
            selectItems={selectItems}
            disabled={disabled}
          />
        </Box>
      );
    });

const TicketFormFields: React.FC<TicketFormFieldsProps> = ({
  formGroups,
  unassignedItems,
  values,
  onChange,
  disabled = false,
}) => {
  const visibleGroups = formGroups
    .filter((g) => g.sortOrder >= 0)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const activeUnassigned = unassignedItems.filter((i) => !i.deleted);

  if (
    visibleGroups.every((g) => g.items.filter((i) => !i.deleted).length === 0) &&
    activeUnassigned.length === 0
  ) {
    return null;
  }

  return (
    <Stack spacing={2.5}>
      {visibleGroups.map((group) => {
        const activeItems = group.items.filter((i) => !i.deleted);
        if (activeItems.length === 0) return null;
        return (
          <Box
            key={group.groupId}
            data-toc-section
            id={`group-${group.groupId}`}
            data-toc-label={group.name}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              {group.name}
            </Typography>
            <Stack spacing={1.5}>{renderItems(group.items, values, onChange, disabled)}</Stack>
          </Box>
        );
      })}
      {activeUnassigned.length > 0 && (
        <Box>
          <Stack spacing={1.5}>{renderItems(activeUnassigned, values, onChange, disabled)}</Stack>
        </Box>
      )}
    </Stack>
  );
};

/** 詳細取得（TicketDetailFormItem[].values）から編集用の値マップを組み立てる */
export const buildValuesFromDetail = (
  groups: { items: { itemKey: string; formType: string; values: string[] }[] }[],
  unassigned: { itemKey: string; formType: string; values: string[] }[],
): Record<string, string> => {
  const map: Record<string, string> = {};
  const put = (itemKey: string, formType: string, vals: string[]) => {
    if (MULTI_FORM_TYPES.includes(formType)) {
      map[itemKey] = JSON.stringify(vals ?? []);
    } else {
      map[itemKey] = (vals && vals[0]) ?? '';
    }
  };
  groups.forEach((g) => g.items.forEach((it) => put(it.itemKey, it.formType, it.values)));
  unassigned.forEach((it) => put(it.itemKey, it.formType, it.values));
  return map;
};

/** 値マップ（itemKey -> string）を API 送信用の TicketFormValueInput[] に変換する */
export const buildFormValues = (
  values: Record<string, string>,
  formTypeByKey: Record<string, string>,
): { itemKey: string; values: string[] }[] => {
  return Object.entries(values)
    .filter(([, v]) => v !== '' && v !== undefined && v !== null)
    .map(([itemKey, v]) => {
      const formType = formTypeByKey[itemKey];
      if (formType && MULTI_FORM_TYPES.includes(formType)) {
        try {
          const arr = JSON.parse(v);
          return { itemKey, values: Array.isArray(arr) ? arr : [] };
        } catch {
          return { itemKey, values: [] };
        }
      }
      return { itemKey, values: [v] };
    })
    .filter((fv) => fv.values.length > 0);
};

export default TicketFormFields;
